import React, { useEffect, useRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';

// 手動指定 CDN 上的 worker 檔案路徑
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.9.179/pdf.worker.min.js`;

interface PdfReaderProps {
  pdfUrl: string;
  setIsLoading: ((isLoading: boolean) => void);
  onError?: () => void;
  onReady?: () => void;
}

const PdfReader: React.FC<PdfReaderProps> = ({ pdfUrl, setIsLoading, onError, onReady }) => {
  const viewerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let disposed = false;
    let loadingTask: ReturnType<typeof pdfjsLib.getDocument> | null = null;

    const loadPdf = async () => {
      if (!viewerRef.current) return;

      viewerRef.current.innerHTML = '';
      setIsLoading(true);

      try {
        loadingTask = pdfjsLib.getDocument({ url: pdfUrl, isEvalSupported: false });
        const pdf = await loadingTask.promise;

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          if (disposed || !viewerRef.current) return;
          const page = await pdf.getPage(pageNum);
          const containerWidth = viewerRef.current.getBoundingClientRect().width;
          const baseViewport = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: containerWidth / baseViewport.width });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          if (!context) throw new Error('PDF canvas context is unavailable.');

          canvas.height = viewport.height;
          canvas.width = viewport.width;
          viewerRef.current.appendChild(canvas);
          await page.render({ canvasContext: context, viewport }).promise;
        }

        if (!disposed) {
          setIsLoading(false);
          onReady?.();
        }
      } catch {
        if (!disposed) {
          setIsLoading(false);
          onError?.();
        }
      }
    };

    void loadPdf();

    return () => {
      disposed = true;
      if (viewerRef.current) viewerRef.current.innerHTML = '';
      if (loadingTask) void loadingTask.destroy();
    };
  }, [pdfUrl, setIsLoading, onError, onReady]);

  return (
    <div className="pdf-modal">
      <div className="pdf-content" ref={viewerRef}></div>
    </div>
  );
};

export default PdfReader;
